/**
 * DockCell — the screener's cell, holding one or two panels.
 *
 * `docs/plans/split-view.md`. The arithmetic is `lib/dock.ts`; this is the
 * layout and the drag.
 *
 * **The drag is local until the pointer lets go.** A divider fires a move per
 * frame, and each one dispatched would re-render the screener grid and the data
 * panel sixty times a second to move a line. The share lives in state while the
 * pointer is down and is handed up once, on release — the persistence layer
 * then sends it as a granular write, the same as the grid's own seams.
 */

import { useRef, useState, type ReactNode } from "react";

import { sharesFromPixels } from "../lib/dock";

export interface DockCellProps {
  /** The panels to show, first to last. One or two. */
  panels: ReactNode[];
  /** One `fr` share per panel. */
  sizes: string[];
  /** `column` stacks them (a side dock); `row` puts them side by side. */
  direction: "row" | "column";
  /** A finished drag. */
  onSizes: (sizes: string[]) => void;
  /** Swap the two. */
  onSwap: () => void;
}

/** The divider's thickness. Wide enough to grab, narrow enough to read as a rule. */
const DIVIDER_PX = 6;

export function DockCell({ panels, sizes, direction, onSizes, onSwap }: DockCellProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState<string[] | null>(null);

  if (panels.length < 2) return <>{panels[0] ?? null}</>;

  const shown = dragging ?? sizes;
  const column = direction === "column";

  const shareAt = (clientX: number, clientY: number): string[] | null => {
    const box = hostRef.current?.getBoundingClientRect();
    if (!box) return null;
    const extent = (column ? box.height : box.width) - DIVIDER_PX;
    const first = (column ? clientY - box.top : clientX - box.left) - DIVIDER_PX / 2;
    return sharesFromPixels(first, extent - first);
  };

  return (
    <div
      ref={hostRef}
      style={{
        display: "grid",
        [column ? "gridTemplateRows" : "gridTemplateColumns"]:
          `minmax(0, ${shown[0]}) ${DIVIDER_PX}px minmax(0, ${shown[1]})`,
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <div style={PANEL}>{panels[0]}</div>
      <div
        role="separator"
        aria-orientation={column ? "horizontal" : "vertical"}
        aria-label="Resize the two panels"
        onPointerDown={(e) => {
          // Only the rule itself starts a drag — the swap button sits on it and
          // must stay a click.
          if (e.target !== e.currentTarget) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          setDragging(sizes);
        }}
        onPointerMove={(e) => {
          if (!dragging) return;
          const next = shareAt(e.clientX, e.clientY);
          if (next) setDragging(next);
        }}
        onPointerUp={(e) => {
          if (!dragging) return;
          e.currentTarget.releasePointerCapture(e.pointerId);
          const next = shareAt(e.clientX, e.clientY) ?? dragging;
          setDragging(null);
          onSizes(next);
        }}
        onPointerCancel={() => setDragging(null)}
        style={{
          position: "relative",
          background: dragging ? "var(--qf-accent)" : "var(--qf-border)",
          cursor: column ? "row-resize" : "col-resize",
          touchAction: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2,
        }}
      >
        <button
          type="button"
          onClick={onSwap}
          title="Swap the two panels"
          aria-label="Swap the two panels"
          style={{
            // Sits on the rule, so the one control that acts on *both* panels
            // is where the two meet rather than in either one's header.
            position: "absolute",
            width: 22,
            height: 16,
            padding: 0,
            border: "1px solid var(--qf-border)",
            borderRadius: 4,
            background: "var(--qf-surface)",
            color: "var(--qf-text-secondary)",
            fontSize: 11,
            lineHeight: "14px",
            cursor: "pointer",
            transform: column ? undefined : "rotate(90deg)",
          }}
        >
          ⇅
        </button>
      </div>
      <div style={PANEL}>{panels[1]}</div>
    </div>
  );
}

/** A dock half with nothing to show yet — never an empty box. */
export function DockEmpty({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        textAlign: "center",
        fontSize: "0.8rem",
        color: "var(--qf-text-secondary)",
        background: "var(--qf-surface-alt)",
      }}
    >
      {children}
    </div>
  );
}

/** A panel's box. `minHeight: 0` or AG Grid measures its content and overflows. */
const PANEL: React.CSSProperties = {
  minWidth: 0,
  minHeight: 0,
  overflow: "hidden",
  position: "relative",
  display: "flex",
  flexDirection: "column",
};
